import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import './NewLookbookV2Page.css';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const OUTFIT_CATEGORIES = {
  ethnic: ['SAREE', 'ANARKALI', 'STRAIGHT_KURTA', 'ALINE_KURTA', 'KURTA_PANTS', 'KURTA_PALAZZO', 'KURTA_SALWAR', 'LEHENGA', 'SHARARA', 'GHARARA', 'PATIALA', 'DHOTI_KURTA', 'ANGRAKHA'],
  western: ['MAXI_DRESS', 'WRAP_DRESS', 'BODYCON_MIDI', 'SLIP_DRESS', 'SHIRT_DRESS', 'COORD_SET', 'CROP_COORD', 'BODYCON_COORD', 'JUMPSUIT', 'WRAP_COAT'],
  fusion: ['CAPE_PALAZZO', 'JACKET_KURTA', 'ASYM_TUNIC', 'KAFTAN', 'PEPLUM_DHOTI', 'CROP_DHOTI', 'LONG_JACKET', 'BLAZER_KURTA', 'DRAPED_CAPE'],
  product: ['FABRIC_EDITORIAL', 'FABRIC_SET_EDITORIAL'],
};

const CATEGORY_LABELS = { ethnic: 'Ethnic', western: 'Western', fusion: 'Fusion', product: 'Product' };
const CATEGORY_ORDER = ['ethnic', 'western', 'fusion', 'product'];

const PAGE_SIZE = 20;
const GENERATED_REVIEW_PAGE_SIZE = 6;

const STATUS_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'complete', label: 'Complete' },
  { key: 'inprogress', label: 'In Progress' },
  { key: 'published', label: 'Published' },
  { key: 'pushpending', label: 'Push Pending' },
  { key: 'approvepending', label: 'Approve Pending' },
  { key: 'notstarted', label: 'Not Started' },
];

const GENERATED_REVIEW_FILTERS = [
  { key: 'needsapproval', label: 'Needs Approval' },
  { key: 'all', label: 'All Generated' },
];

// ---------------------------------------------------------------------------
// Helper functions
// ---------------------------------------------------------------------------

function getFabricOverallStatus(fabric) {
  const allCodes = fabric.eligibleOutfitCodes || [];
  const codes = getActiveEligibleCodes(fabric);
  if (!allCodes.length) return 'notstarted';
  if (!codes.length) return 'complete';
  const gen = fabric.generation || {};
  const done = codes.filter((c) => gen[c]?.status === 'done').length;
  if (done === codes.length) return 'complete';
  if (done > 0 || codes.some((c) => gen[c]?.status === 'in_progress')) return 'inprogress';
  return 'notstarted';
}

function hasApprovePendingOutfit(fabric) {
  const codes = getActiveEligibleCodes(fabric);
  const gen = fabric.generation || {};
  return codes.some((code) => {
    const item = gen[code] || {};
    if (item.publishStatus === 'published') return false;
    if (item.status !== 'done') return false;
    if (item.approvalStatus === 'rejected') return false;
    return true;
  });
}

function hasPublishedOutfit(fabric) {
  const codes = getActiveEligibleCodes(fabric);
  const gen = fabric.generation || {};
  return codes.some((code) => String(gen[code]?.publishStatus || '').trim() === 'published');
}

function hasPushPendingOutfit(fabric) {
  const codes = getActiveEligibleCodes(fabric);
  const gen = fabric.generation || {};
  return codes.some((code) => {
    const item = gen[code] || {};
    if (item.status !== 'done') return false;
    if (String(item.approvalStatus || '').trim() !== 'approved') return false;
    return String(item.publishStatus || '').trim() !== 'published';
  });
}

function getNextPendingOutfit(fabric) {
  const codes = getActiveEligibleCodes(fabric);
  const gen = fabric.generation || {};
  const pending = codes.filter((c) => !gen[c] || gen[c].status === 'pending');
  return pending.length ? pending[0] : null;
}

function getActiveEligibleCodes(fabric) {
  const codes = Array.isArray(fabric?.eligibleOutfitCodes) ? fabric.eligibleOutfitCodes : [];
  const gen = fabric?.generation || {};
  return codes.filter((code) => gen[code]?.approvalStatus !== 'rejected');
}

function formatDate(v) {
  if (!v) return '—';
  // Handle Firestore Timestamp objects
  if (v && typeof v === 'object' && typeof v.toDate === 'function') {
    return v.toDate().toLocaleDateString();
  }
  // Handle Firestore { _seconds, _nanoseconds } plain objects
  if (v && typeof v === 'object' && typeof v._seconds === 'number') {
    return new Date(v._seconds * 1000).toLocaleDateString();
  }
  const d = new Date(v);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString();
}

function getOutfitLabel(code, outfitTypes) {
  const found = outfitTypes.find((o) => o.code === code);
  if (found) return found.label;
  // Fallback: prettify code
  return code
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

function getCategoryForCode(code) {
  for (const [cat, codes] of Object.entries(OUTFIT_CATEGORIES)) {
    if (codes.includes(code)) return cat;
  }
  return 'product';
}

function getLookbookV2RowId(fabric) {
  return String(fabric?.rowId || fabric?.id || '').trim();
}

function getLookbookV2FabricSpecId(fabric) {
  return String(fabric?.fabricSpecId || '').trim();
}

function getLookbookV2GenerateKey(rowId, outfitCode) {
  return `${String(rowId || '').trim()}_${String(outfitCode || '').trim()}`;
}

function mergeLookbookV2Rows(base, incoming) {
  return {
    ...base,
    ...incoming,
    rowId: incoming?.rowId || base?.rowId,
    productId: incoming?.productId || base?.productId,
    variantId: incoming?.variantId ?? base?.variantId ?? null,
  };
}

// ---------------------------------------------------------------------------
// StatusBadge
// ---------------------------------------------------------------------------

function StatusBadge({ status }) {
  const map = {
    complete: { label: 'Complete', cls: 'nlbv2-badge nlbv2-badge--complete' },
    inprogress: { label: 'In Progress', cls: 'nlbv2-badge nlbv2-badge--inprogress' },
    notstarted: { label: 'Not Started', cls: 'nlbv2-badge nlbv2-badge--notstarted' },
    done: { label: 'Done', cls: 'nlbv2-badge nlbv2-badge--complete' },
    pending: { label: 'Pending', cls: 'nlbv2-badge nlbv2-badge--notstarted' },
    in_progress: { label: 'Generating', cls: 'nlbv2-badge nlbv2-badge--inprogress' },
  };
  const e = map[status] || map.notstarted;
  return <span className={e.cls}>{e.label}</span>;
}

// ---------------------------------------------------------------------------
// Pagination
// ---------------------------------------------------------------------------

function Pagination({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;
  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= page - 2 && i <= page + 2)) pages.push(i);
    else if (pages[pages.length - 1] !== '…') pages.push('…');
  }
  return (
    <div className="nlbv2-pagination">
      <button className="nlbv2-page-btn" disabled={page === 1} onClick={() => onChange(page - 1)}>‹</button>
      {pages.map((p, i) =>
        p === '…'
          ? <span key={`e${i}`} className="nlbv2-page-ellipsis">…</span>
          : <button key={p} className={`nlbv2-page-btn${p === page ? ' nlbv2-page-btn--active' : ''}`} onClick={() => onChange(p)}>{p}</button>
      )}
      <button className="nlbv2-page-btn" disabled={page === totalPages} onClick={() => onChange(page + 1)}>›</button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PublishQueuePanel
// ---------------------------------------------------------------------------

function PublishQueuePanel({ authedFetch, refreshToken = 0 }) {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [ticking, setTicking] = useState(false);
  const [tickError, setTickError] = useState('');

  const fetchStatus = async () => {
    try {
      const res = await authedFetch('/api/v1/lookbook-v2/publish-queue/status');
      if (res.ok) { const d = await res.json(); if (d.success) setStatus(d); }
    } catch (_) {}
  };

  useEffect(() => { fetchStatus(); }, [fetchStatus, refreshToken]);

  const handleTick = async () => {
    setTicking(true);
    setTickError('');
    try {
      const res = await authedFetch('/api/v1/lookbook-v2/publish-queue/tick', { method: 'POST' });
      const d = await res.json();
      if (d.success) {
        await fetchStatus();
      } else {
        setTickError(d.message || 'Publish failed.');
      }
    } catch (error) {
      setTickError(error?.message || 'Publish failed.');
    } finally { setTicking(false); }
  };

  const pendingCount = status?.pendingCount ?? '…';
  const summary = status?.summary || {};

  return (
    <div className="nlbv2-publish-panel">
      <div className="nlbv2-publish-panel-header" onClick={() => setOpen((o) => !o)}>
        <span className="nlbv2-publish-panel-title">
          📤 Publish Queue
          <span className={`nlbv2-publish-count${pendingCount > 0 ? ' nlbv2-publish-count--active' : ''}`}>
            {pendingCount} pending
          </span>
        </span>
        <span className="nlbv2-publish-panel-toggle">{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div className="nlbv2-publish-panel-body">
          <p className="nlbv2-publish-info">
            Approved images publish automatically <strong>Daily 00:30–01:30 IST</strong> every 2 min, plus <strong>Monday 00:00–05:59 IST</strong> every 10 min with up to 25 images per run via cloud scheduler.
          </p>
          <div className="nlbv2-publish-summary">
            <span className="nlbv2-publish-summary-chip nlbv2-publish-summary-chip--pending">
              {summary.pending ?? 0} pending
            </span>
            <span className="nlbv2-publish-summary-chip nlbv2-publish-summary-chip--done">
              {summary.published ?? 0} published
            </span>
            <span className="nlbv2-publish-summary-chip nlbv2-publish-summary-chip--failed">
              {summary.failed ?? 0} failed
            </span>
          </div>
          {tickError ? (
            <div className="nlbv2-outfit-card-error" style={{ marginTop: 8 }}>
              ⚠ {tickError}
            </div>
          ) : null}
          {status?.pending?.length > 0 && (
            <div className="nlbv2-publish-list">
              <div className="nlbv2-publish-list-label">Pending ({status.pending.length})</div>
              {status.pending.map((item) => (
                <div key={item.id} className="nlbv2-publish-item">
                  <span className="nlbv2-publish-item-name">{item.fabricName} · {item.outfitCode}</span>
                  <span className="nlbv2-publish-item-status nlbv2-publish-item-status--pending">Pending</span>
                </div>
              ))}
            </div>
          )}
          {status?.recentPublished?.length > 0 && (
            <div className="nlbv2-publish-list" style={{ marginTop: 8 }}>
              <div className="nlbv2-publish-list-label">Recently Published</div>
              {status.recentPublished.slice(0, 3).map((item) => (
                <div key={item.id} className="nlbv2-publish-item">
                  <span className="nlbv2-publish-item-name">{item.fabricName} · {item.outfitCode}</span>
                  <span className="nlbv2-publish-item-status nlbv2-publish-item-status--done">Published</span>
                </div>
              ))}
            </div>
          )}
          {status?.recentFailed?.length > 0 && (
            <div className="nlbv2-publish-list" style={{ marginTop: 8 }}>
              <div className="nlbv2-publish-list-label">Recent Failed</div>
              {status.recentFailed.slice(0, 3).map((item) => (
                <div key={item.id} className="nlbv2-publish-item">
                  <span className="nlbv2-publish-item-name">{item.fabricName} · {item.outfitCode}</span>
                  <span className="nlbv2-publish-item-status nlbv2-publish-item-status--failed">Failed</span>
                </div>
              ))}
            </div>
          )}
          <div className="nlbv2-publish-actions">
            <button className="nlbv2-btn nlbv2-btn--sm" onClick={fetchStatus}>Refresh</button>
            <button className="nlbv2-btn nlbv2-btn--sm nlbv2-btn--primary" onClick={handleTick} disabled={ticking || pendingCount === 0}>
              {ticking ? 'Publishing…' : 'Publish One Now'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function GeneratedReviewAccordion({
  scopeType,
  scopeValue,
  authedFetch,
  outfitTypes,
  generating,
  onGenerated,
  onApprovalChange,
  onLightbox,
  refreshToken = 0,
}) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('needsapproval');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, totalCount: 0 });
  const [summary, setSummary] = useState({ generatedCount: 0, needsApprovalCount: 0 });

  const fetchReview = useCallback(async () => {
    if (!scopeValue || !open) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(GENERATED_REVIEW_PAGE_SIZE),
        mode,
      });
      if (scopeType === 'productType') params.set('productType', scopeValue);
      else params.set('collectionId', scopeValue);
      const res = await authedFetch(`/api/v1/lookbook-v2/generated-review?${params.toString()}`);
      if (!res.ok) throw new Error('Failed to load generated review.');
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Failed to load generated review.');
      setItems(data.items || []);
      setPagination(data.pagination || { page: 1, totalPages: 1, totalCount: 0 });
      setSummary(data.summary || { generatedCount: 0, needsApprovalCount: 0 });
    } catch (err) {
      setError(err.message || 'Failed to load generated review.');
    } finally {
      setLoading(false);
    }
  }, [authedFetch, mode, open, page, scopeType, scopeValue]);

  useEffect(() => {
    if (!open) return;
    fetchReview();
  }, [fetchReview, refreshToken]);

  useEffect(() => {
    setPage(1);
    setItems([]);
    setPagination({ page: 1, totalPages: 1, totalCount: 0 });
  }, [scopeType, scopeValue, mode]);

  const activeCount = mode === 'needsapproval' ? (summary.needsApprovalCount ?? 0) : (summary.generatedCount ?? 0);

  return (
    <div className="nlbv2-generated-review">
      <button className="nlbv2-generated-review-toggle" onClick={() => setOpen((v) => !v)}>
        <span>{open ? '▼' : '▶'}</span>
        Generated Review
        <span className="nlbv2-generated-review-badge">{activeCount} products</span>
      </button>
      {open && (
        <div className="nlbv2-generated-review-body">
          <div className="nlbv2-generated-review-toolbar">
            <div className="nlbv2-filter-pills nlbv2-filter-pills--compact">
              {GENERATED_REVIEW_FILTERS.map((filter) => (
                <button
                  key={filter.key}
                  className={`nlbv2-pill${mode === filter.key ? ' nlbv2-pill--active' : ''}`}
                  onClick={() => setMode(filter.key)}
                >
                  {filter.label}
                  <span className="nlbv2-pill-count">
                    {filter.key === 'needsapproval' ? (summary.needsApprovalCount ?? 0) : (summary.generatedCount ?? 0)}
                  </span>
                </button>
              ))}
            </div>
            <button className="nlbv2-btn nlbv2-btn--sm" onClick={fetchReview} disabled={loading || !scopeValue}>
              {loading ? 'Loading…' : 'Refresh'}
            </button>
          </div>
          {error && <div className="nlbv2-error" style={{ marginBottom: 12 }}>{error}</div>}
          {!scopeValue ? (
            <div className="nlbv2-placeholder">Select a product type or collection to review generated outputs.</div>
          ) : loading && items.length === 0 ? (
            <div className="nlbv2-loading">Loading generated review…</div>
          ) : items.length === 0 ? (
            <div className="nlbv2-placeholder">No generated products match this review filter.</div>
          ) : (
            <>
              <div className="nlbv2-generated-review-grid">
                {items.map((fabric) => (
                  <div key={getLookbookV2RowId(fabric)} className="nlbv2-generated-review-item">
                    <div className="nlbv2-generated-review-item-header">
                      {fabric.productImageUrl ? (
                        <img src={fabric.productImageUrl} alt={fabric.fabric} className="nlbv2-generated-review-thumb" />
                      ) : (
                        <div className="nlbv2-generated-review-thumb nlbv2-generated-review-thumb--empty" />
                      )}
                      <div className="nlbv2-generated-review-meta">
                        <div className="nlbv2-generated-review-title">{fabric.fabric}</div>
                        <div className="nlbv2-generated-review-subtitle">{fabric.skuPrefix || '—'}{fabric.productTitle ? ` · ${fabric.productTitle}` : ''}</div>
                      </div>
                      <StatusBadge status={getFabricOverallStatus(fabric)} />
                    </div>
                    <div className="nlbv2-outfit-cards nlbv2-outfit-cards--review">
                      {(fabric.eligibleOutfitCodes || []).map((code) => (
                        <OutfitCard
                          key={`${getLookbookV2RowId(fabric)}_${code}`}
                          code={code}
                          label={getOutfitLabel(code, outfitTypes)}
                          data={fabric.generation?.[code]}
                          rowId={getLookbookV2RowId(fabric)}
                          fabricId={getLookbookV2FabricSpecId(fabric)}
                          productId={fabric.productId}
                          outfitTypes={outfitTypes}
                          onGenerated={onGenerated}
                          onApprovalChange={onApprovalChange}
                          authedFetch={authedFetch}
                          isGenerating={generating.has(getLookbookV2GenerateKey(getLookbookV2RowId(fabric), code))}
                          onLightbox={onLightbox}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <Pagination page={pagination.page || page} totalPages={pagination.totalPages || 1} onChange={setPage} />
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AutoQueuePanel
// ---------------------------------------------------------------------------

// productType currently active in this scheduler — hard-coded to match Cloud Scheduler job.
// To add more types: create another Cloud Scheduler job pointing to lookbookV2GenerateByType.
const LOOKBOOK_SCHEDULER_TYPES = ['Digital Printed Fabric', 'Fabric Set'];

function AutoQueuePanel({ authedFetch, onQueueChange, outfitTypes, scopeType, scopeValue }) {
  const [queue, setQueue] = useState(null);
  const mountedRef = useRef(true);
  
  const activeType = useMemo(() => {
    if (scopeType === 'productType' && scopeValue && LOOKBOOK_SCHEDULER_TYPES.includes(scopeValue)) {
      return scopeValue;
    }
    return LOOKBOOK_SCHEDULER_TYPES[0];
  }, [scopeType, scopeValue]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await authedFetch(`/api/v1/lookbook-v2/auto-queue/status?productType=${encodeURIComponent(activeType)}`);
      if (!res.ok) return;
      const data = await res.json();
      if (data.success && mountedRef.current) {
        setQueue(data.queue || {});
        if (onQueueChange) onQueueChange(data.queue || {});
      }
    } catch (_) {}
  }, [authedFetch, onQueueChange, activeType]);

  // Poll every 30s
  useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, 30000);
    return () => clearInterval(id);
  }, [fetchStatus]);

  const isActive = Boolean(queue?.currentFabricId);

  const nextInLabel = useMemo(() => {
    const anchor = queue?.lastTickStartedAt || queue?.lastProcessedAt;
    if (!anchor) return null;
    const last = new Date(anchor);
    if (isNaN(last.getTime())) return null;
    const nextAt = new Date(last.getTime() + 4 * 60 * 1000);
    const diff = nextAt.getTime() - Date.now();
    if (diff <= 0) return 'any moment';
    const m = Math.floor(diff / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  }, [queue]);

  const [, forceUpdate] = useState(0);
  useEffect(() => {
    if (!isActive) return;
    const id = setInterval(() => forceUpdate((n) => n + 1), 5000);
    return () => clearInterval(id);
  }, [isActive]);

  const currentOutfitLabel = useMemo(() => {
    if (!queue?.currentOutfitType) return null;
    return getOutfitLabel(queue.currentOutfitType, outfitTypes || []);
  }, [queue?.currentOutfitType, outfitTypes]);

  const lastOutfitLabel = useMemo(() => {
    if (!queue?.lastProcessedOutfitType) return null;
    return getOutfitLabel(queue.lastProcessedOutfitType, outfitTypes || []);
  }, [queue?.lastProcessedOutfitType, outfitTypes]);

  return (
    <div className={`nlbv2-queue-panel${isActive ? ' nlbv2-queue-panel--running' : ''}`}>
      <div className="nlbv2-queue-header">
        <div className="nlbv2-queue-title">
          <span className={`nlbv2-queue-dot${isActive ? ' nlbv2-queue-dot--on' : ''}`} />
          Image Generation Queue
          {isActive && currentOutfitLabel && (
            <span className="nlbv2-queue-countdown">Outfit: {currentOutfitLabel}</span>
          )}
          {nextInLabel && (
            <span className="nlbv2-queue-countdown">next in {nextInLabel}</span>
          )}
        </div>
        <div className="nlbv2-queue-controls">
          <span className="nlbv2-queue-mode-tag">
            lookbookV2GenerateByType · every 4 min · {LOOKBOOK_SCHEDULER_TYPES.join(', ')}
          </span>
        </div>
      </div>

      {isActive && queue?.currentFabricTitle && (
        <div className="nlbv2-queue-stats">
          <div className="nlbv2-queue-stat nlbv2-queue-stat--active">
            <span className="nlbv2-queue-stat-label">Generating</span>
            <span>{queue.currentFabricTitle}</span>
            {currentOutfitLabel && (
              <span className="nlbv2-queue-stat-sub">Outfit: {currentOutfitLabel}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ScopeSelector
// ---------------------------------------------------------------------------

function ScopeSelector({
  scopeType,
  scopeValue,
  productTypes,
  collections,
  onChange,
  onPushBatch,
  batchPublishing = false,
  batchPublishMessage = '',
}) {
  return (
    <div className="nlbv2-scope-selector">
      <div className="nlbv2-scope-toggle">
        <button
          className={`nlbv2-scope-btn${scopeType === 'productType' ? ' nlbv2-scope-btn--active' : ''}`}
          onClick={() => onChange('productType', '')}
        >
          Product Type
        </button>
        <button
          className={`nlbv2-scope-btn${scopeType === 'collection' ? ' nlbv2-scope-btn--active' : ''}`}
          onClick={() => onChange('collection', '')}
        >
          Collection
        </button>
      </div>
      {scopeType === 'productType' ? (
        <div className="nlbv2-scope-inline">
          <select
            className="nlbv2-select"
            value={scopeValue}
            onChange={(e) => onChange('productType', e.target.value)}
          >
            <option value="">— Select product type —</option>
            {productTypes.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <button
            type="button"
            className="nlbv2-btn nlbv2-btn--sm"
            onClick={onPushBatch}
            disabled={!scopeValue || batchPublishing}
            title={!scopeValue ? 'Select a product type first' : 'Publish up to 20 approved queued images for this product type'}
          >
            {batchPublishing ? 'Pushing…' : 'Push 20 Images'}
          </button>
        </div>
      ) : scopeType === 'collection' ? (
        <select
          className="nlbv2-select"
          value={scopeValue}
          onChange={(e) => onChange('collection', e.target.value)}
        >
          <option value="">— Select collection —</option>
          {collections.map((c) => (
            <option key={c.id} value={c.id}>{c.title}</option>
          ))}
        </select>
      ) : (
        <div className="nlbv2-scope-hint">Select Product Type or Collection to load products.</div>
      )}
      {batchPublishMessage ? <div className="nlbv2-scope-hint">{batchPublishMessage}</div> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PromptEditor (2-level tabs)
// ---------------------------------------------------------------------------

function PromptEditor({ outfitTypes, authedFetch }) {
  const [open, setOpen] = useState(false);
  const [prompts, setPrompts] = useState({});
  const [preamble, setPreamble] = useState('');
  const [activeCat, setActiveCat] = useState('ethnic');
  const [activeCode, setActiveCode] = useState(OUTFIT_CATEGORIES.ethnic[0]);
  const [saving, setSaving] = useState(false);
  const [savingPreamble, setSavingPreamble] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPreamble, setShowPreamble] = useState(true);

  const loadPrompts = useCallback(async () => {
    setLoading(true);
    try {
      const [promptsRes, preambleRes] = await Promise.all([
        authedFetch('/api/v1/lookbook-v2/prompts'),
        authedFetch('/api/v1/lookbook-v2/preamble'),
      ]);
      if (promptsRes.ok) {
        const d = await promptsRes.json();
        if (d.success) setPrompts(d.prompts || {});
      }
      if (preambleRes.ok) {
        const d = await preambleRes.json();
        if (d.success) setPreamble(d.preamble || '');
      }
    } finally {
      setLoading(false);
    }
  }, [authedFetch]);

  useEffect(() => {
    if (open) loadPrompts();
  }, [open, loadPrompts]);

  // When category changes, select first code in category
  const handleCatChange = (cat) => {
    setActiveCat(cat);
    setActiveCode(OUTFIT_CATEGORIES[cat][0]);
  };

  const handleSaveAll = async () => {
    setSaving(true);
    setMessage('');
    try {
      const res = await authedFetch('/api/v1/lookbook-v2/prompts', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(prompts),
      });
      if (!res.ok) { setMessage('Save failed.'); return; }
      const data = await res.json();
      setMessage(data.success ? 'Saved all prompts.' : data.message || 'Save failed.');
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleSavePreamble = async () => {
    setSavingPreamble(true);
    setMessage('');
    try {
      const res = await authedFetch('/api/v1/lookbook-v2/preamble', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preamble }),
      });
      if (!res.ok) { setMessage('Preamble save failed.'); return; }
      const data = await res.json();
      setMessage(data.success ? 'Preamble saved.' : data.message || 'Save failed.');
    } finally {
      setSavingPreamble(false);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleResetPrompt = async () => {
    if (!activeCode) return;
    // Clear current code's prompt and reload from server
    setPrompts((prev) => {
      const next = { ...prev };
      delete next[activeCode];
      return next;
    });
    await loadPrompts();
    setMessage(`Reset ${activeCode} to default.`);
    setTimeout(() => setMessage(''), 3000);
  };

  const promptCount = Object.keys(prompts).length;

  return (
    <div className="nlbv2-prompt-editor">
      <button className="nlbv2-prompt-toggle" onClick={() => setOpen((v) => !v)}>
        <span>{open ? '▼' : '▶'}</span>
        Generation Prompts
        <span className="nlbv2-prompt-badge">{promptCount} prompts</span>
      </button>
      {open && (
        <div className="nlbv2-prompt-body">
          {/* Universal Preamble section */}
          <div className="nlbv2-preamble-section">
            <div className="nlbv2-preamble-header" onClick={() => setShowPreamble((v) => !v)}>
              <span className="nlbv2-preamble-toggle-icon">{showPreamble ? '▼' : '▶'}</span>
              <span className="nlbv2-preamble-title">Universal Preamble</span>
              <span className="nlbv2-preamble-hint">Automatically prepended before every outfit prompt. Use <code>{'{FABRIC_NAME}'}</code> for fabric name.</span>
            </div>
            {showPreamble && (
              <div className="nlbv2-preamble-body">
                <textarea
                  className="nlbv2-prompt-textarea nlbv2-preamble-textarea"
                  value={preamble}
                  onChange={(e) => setPreamble(e.target.value)}
                  spellCheck={false}
                  rows={10}
                  placeholder="Universal preamble applied before every prompt…"
                />
                <div className="nlbv2-preamble-footer">
                  <button
                    className="nlbv2-btn nlbv2-btn--primary"
                    onClick={handleSavePreamble}
                    disabled={savingPreamble}
                  >
                    {savingPreamble ? 'Saving…' : 'Save Preamble'}
                  </button>
                  {message && <span className="nlbv2-prompt-msg">{message}</span>}
                </div>
              </div>
            )}
          </div>
          <div className="nlbv2-preamble-divider">↓ Outfit-Specific Prompts (appended after preamble)</div>
          {/* Level 1: Category tabs */}
          <div className="nlbv2-cat-tabs nlbv2-prompt-cat-tabs">
            {CATEGORY_ORDER.map((cat) => (
              <button
                key={cat}
                className={`nlbv2-cat-tab${activeCat === cat ? ' nlbv2-cat-tab--active' : ''}`}
                onClick={() => handleCatChange(cat)}
              >
                {CATEGORY_LABELS[cat]}
              </button>
            ))}
          </div>
          {/* Level 2: Outfit-specific tabs */}
          <div className="nlbv2-prompt-outfit-tabs">
            {OUTFIT_CATEGORIES[activeCat].map((code) => {
              const label = getOutfitLabel(code, outfitTypes);
              return (
                <button
                  key={code}
                  className={`nlbv2-prompt-outfit-tab${activeCode === code ? ' nlbv2-prompt-outfit-tab--active' : ''}`}
                  onClick={() => setActiveCode(code)}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <div className="nlbv2-prompt-content">
            {loading ? (
              <div style={{ color: '#888', fontSize: 13, padding: '8px 0' }}>Loading…</div>
            ) : (
              <>
                <div className="nlbv2-prompt-code-label">{activeCode}</div>
                <textarea
                  className="nlbv2-prompt-textarea"
                  value={prompts[activeCode] || ''}
                  onChange={(e) =>
                    setPrompts((prev) => ({ ...prev, [activeCode]: e.target.value }))
                  }
                  spellCheck={false}
                  placeholder={`Enter prompt for ${getOutfitLabel(activeCode, outfitTypes)}…`}
                />
                <div className="nlbv2-prompt-footer">
                  <div className="nlbv2-prompt-actions">
                    <button
                      className="nlbv2-btn nlbv2-btn--primary"
                      onClick={handleSaveAll}
                      disabled={saving}
                    >
                      {saving ? 'Saving…' : 'Save All Prompts'}
                    </button>
                    <button
                      className="nlbv2-btn"
                      onClick={handleResetPrompt}
                      disabled={saving}
                      style={{ marginLeft: 8 }}
                    >
                      Reset This Prompt
                    </button>
                  </div>
                  {message && <span className="nlbv2-prompt-msg">{message}</span>}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ConfigureOutfitsModal (inline overlay panel)
// ---------------------------------------------------------------------------

function ConfigureOutfitsModal({ fabrics, initialFabricId, outfitTypes, authedFetch, onClose, onSaved }) {
  const [activeFabricId, setActiveFabricId] = useState(() => {
    const firstId = String(initialFabricId || fabrics?.[0]?.fabricSpecId || fabrics?.[0]?.id || '').trim();
    return firstId;
  });
  const [saving, setSaving] = useState(false);
  const [initing, setIniting] = useState(false);
  const [message, setMessage] = useState('');
  const activeFabric = useMemo(
    () => (fabrics || []).find((item) => getLookbookV2FabricSpecId(item) === activeFabricId || String(item?.id || '').trim() === activeFabricId) || null,
    [fabrics, activeFabricId]
  );
  const pendingFabrics = useMemo(
    () => (fabrics || []).filter((item) => !Array.isArray(item?.eligibleOutfitCodes) || item.eligibleOutfitCodes.length === 0),
    [fabrics]
  );
  const fabricSpecId = getLookbookV2FabricSpecId(activeFabric) || String(activeFabric?.id || '').trim();
  const [selected, setSelected] = useState(new Set(activeFabric?.eligibleOutfitCodes || []));

  useEffect(() => {
    setSelected(new Set(activeFabric?.eligibleOutfitCodes || []));
    setMessage('');
  }, [activeFabricId, activeFabric]);

  const allCodes = useMemo(() => {
    const codes = [];
    CATEGORY_ORDER.forEach((cat) => codes.push(...OUTFIT_CATEGORIES[cat]));
    return codes;
  }, []);

  const handleToggle = (code) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const handleSelectAll = (cat) => {
    setSelected((prev) => {
      const next = new Set(prev);
      OUTFIT_CATEGORIES[cat].forEach((c) => next.add(c));
      return next;
    });
  };

  const handleClearAll = (cat) => {
    setSelected((prev) => {
      const next = new Set(prev);
      OUTFIT_CATEGORIES[cat].forEach((c) => next.delete(c));
      return next;
    });
  };

  const handleSave = async () => {
    if (!fabricSpecId) {
      setMessage('No fabric specification selected.');
      return;
    }
    setSaving(true);
    setMessage('');
    try {
      const res = await authedFetch(`/api/v1/lookbook-v2/fabric/${fabricSpecId}/outfit-types`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ outfitTypes: Array.from(selected) }),
      });
      if (!res.ok) { setMessage('Failed to save outfit types.'); return; }
      const data = await res.json();
      if (!data.success) { setMessage(data.message || 'Failed to save outfit types.'); return; }

      // After saving, call init
      setIniting(true);
      try {
        await authedFetch(`/api/v1/lookbook-v2/fabric/${fabricSpecId}/init`, { method: 'POST' });
      } catch (_) {}
      setIniting(false);

      setMessage('Saved.');
      if (onSaved) onSaved();
    } catch (err) {
      setMessage(err.message || 'Error saving.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="nlbv2-configure-overlay" onClick={onClose}>
      <div className="nlbv2-configure-panel" onClick={(e) => e.stopPropagation()}>
        <div className="nlbv2-configure-header">
          <div>
            <strong>Configure Outfit Types</strong>
            <div className="nlbv2-configure-fabric-name">
              {activeFabric ? `${activeFabric.fabric} (${activeFabric.skuPrefix || '—'})` : 'Select a fabric spec'}
            </div>
          </div>
          <button className="nlbv2-panel-close" onClick={onClose}>✕</button>
        </div>
        <div className="nlbv2-configure-body">
          {pendingFabrics.length > 0 && (
            <div
              style={{
                marginBottom: 12,
                padding: '10px 12px',
                borderRadius: 10,
                border: '1px solid #ffe58f',
                background: '#fffbe6',
                color: '#8c6d1f',
                fontSize: 13,
                lineHeight: 1.5,
              }}
            >
              <strong>Pending configuration:</strong>{' '}
              {pendingFabrics.map((item) => `${item.fabric} (${item.skuPrefix || '—'})`).join(', ')}
            </div>
          )}
          <div style={{ marginBottom: 12 }}>
            <select
              className="nlbv2-select"
              value={activeFabricId}
              onChange={(e) => setActiveFabricId(e.target.value)}
            >
              {(fabrics || []).map((item) => {
                const optionId = getLookbookV2FabricSpecId(item) || String(item?.id || '').trim();
                return (
                  <option key={optionId} value={optionId}>
                    {item.fabric} ({item.skuPrefix || '—'})
                  </option>
                );
              })}
            </select>
          </div>
          <div className="nlbv2-configure-selection-info">
            {selected.size} / {allCodes.length} selected
          </div>
          {CATEGORY_ORDER.map((cat) => (
            <div key={cat} className="nlbv2-configure-cat-section">
              <div className="nlbv2-configure-cat-header">
                <span className="nlbv2-configure-cat-label">{CATEGORY_LABELS[cat]}</span>
                <button
                  className="nlbv2-btn nlbv2-btn--sm"
                  onClick={() => handleSelectAll(cat)}
                >
                  All
                </button>
                <button
                  className="nlbv2-btn nlbv2-btn--sm"
                  onClick={() => handleClearAll(cat)}
                  style={{ marginLeft: 4 }}
                >
                  None
                </button>
              </div>
              <div className="nlbv2-configure-checkboxes">
                {OUTFIT_CATEGORIES[cat].map((code) => {
                  const label = getOutfitLabel(code, outfitTypes);
                  return (
                    <label key={code} className="nlbv2-configure-checkbox-label">
                      <input
                        type="checkbox"
                        checked={selected.has(code)}
                        onChange={() => handleToggle(code)}
                      />
                      <span>{label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <div className="nlbv2-configure-footer">
          {message && <span className="nlbv2-prompt-msg" style={{ marginRight: 12 }}>{message}</span>}
          <button className="nlbv2-btn" onClick={onClose} style={{ marginRight: 8 }}>
            Cancel
          </button>
          <button
            className="nlbv2-btn nlbv2-btn--primary"
            onClick={handleSave}
            disabled={saving || initing || !fabricSpecId}
          >
            {initing ? 'Initializing…' : saving ? 'Saving…' : 'Save & Initialize'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// OutfitCard
// ---------------------------------------------------------------------------

function OutfitCard({ code, label, data, rowId, fabricId, productId, outfitTypes, onGenerated, onApprovalChange, authedFetch, isGenerating, onLightbox }) {
  const status = data?.status || 'pending';
  const approvalStatus = data?.approvalStatus || null;
  const publishStatus = data?.publishStatus || null;
  const hasGeneratedImage = Boolean(data?.imageUrl) || status === 'done';
  const [approving, setApproving] = React.useState(false);

  const cardCls = [
    'nlbv2-outfit-card',
    status === 'done' ? 'nlbv2-outfit-card--done' : '',
    status === 'in_progress' ? 'nlbv2-outfit-card--inprogress' : '',
    isGenerating ? 'nlbv2-outfit-card--generating' : '',
    approvalStatus === 'approved' ? 'nlbv2-outfit-card--approved' : '',
  ].filter(Boolean).join(' ');

  const handleGenerate = async () => {
    if (!onGenerated) return;
    try {
      const res = await authedFetch('/api/v1/lookbook-v2/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rowId,
          fabricSpecId: fabricId,
          outfitCode: code,
          productId: productId || undefined,
          variantId: data?.variantId || undefined,
          ownerSku: data?.ownerSku || undefined,
        }),
      });
      if (!res.ok) return;
      const responseData = await res.json();
      if (responseData.success) onGenerated(rowId, fabricId, code, responseData);
    } catch (_) {}
  };

  const handleApprove = async () => {
    setApproving(true);
    try {
      const res = await authedFetch(`/api/v1/lookbook-v2/fabric/${fabricId}/outfit/${code}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entryId: data?.entryId || undefined }),
      });
      if (res.ok && onApprovalChange) onApprovalChange(rowId, fabricId, code, { approvalStatus: 'approved', publishStatus: 'queued' });
    } catch (_) {} finally { setApproving(false); }
  };

  const handleReject = async () => {
    setApproving(true);
    try {
      const res = await authedFetch(`/api/v1/lookbook-v2/fabric/${fabricId}/outfit/${code}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entryId: data?.entryId || undefined }),
      });
      if (res.ok && onApprovalChange) onApprovalChange(rowId, fabricId, code, { approvalStatus: 'rejected', publishStatus: null });
    } catch (_) {} finally { setApproving(false); }
  };

  const handleUndoReject = async () => {
    setApproving(true);
    try {
      const res = await authedFetch(`/api/v1/lookbook-v2/fabric/${fabricId}/outfit/${code}/undo-reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entryId: data?.entryId || undefined }),
      });
      if (res.ok && onApprovalChange) onApprovalChange(rowId, fabricId, code, { approvalStatus: null, publishStatus: null });
    } catch (_) {} finally { setApproving(false); }
  };

  const handleUnapprove = async () => {
    setApproving(true);
    try {
      const res = await authedFetch(`/api/v1/lookbook-v2/fabric/${fabricId}/outfit/${code}/unapprove`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entryId: data?.entryId || undefined }),
      });
      if (res.ok && onApprovalChange) onApprovalChange(rowId, fabricId, code, { approvalStatus: null, publishStatus: null });
    } catch (_) {} finally { setApproving(false); }
  };

  // Approval status badge
  const approvalBadge = () => {
    if (publishStatus === 'published') return <span className="nlbv2-approval-badge nlbv2-approval-badge--published">Published</span>;
    if (publishStatus === 'queued' || publishStatus === 'pending') return <span className="nlbv2-approval-badge nlbv2-approval-badge--queued">In Queue</span>;
    if (publishStatus === 'failed') return <span className="nlbv2-approval-badge nlbv2-approval-badge--failed">Pub Failed</span>;
    if (approvalStatus === 'approved') return <span className="nlbv2-approval-badge nlbv2-approval-badge--approved">Approved</span>;
    if (approvalStatus === 'rejected') return <span className="nlbv2-approval-badge nlbv2-approval-badge--rejected">Rejected</span>;
    return null;
  };

  return (
    <div className={cardCls}>
      <div className="nlbv2-outfit-card-header">
        <span className="nlbv2-outfit-card-label">{label}</span>
        <span className="nlbv2-outfit-card-status">
          {status === 'done' && !approvalStatus && '✓'}
          {status === 'in_progress' && (
            <span className="nlbv2-queue-dot nlbv2-queue-dot--on" style={{ width: 7, height: 7, display: 'inline-block' }} />
          )}
          {approvalBadge()}
        </span>
      </div>
      <div className="nlbv2-outfit-card-img-wrap">
        {data?.imageUrl ? (
          <img
            src={data.imageUrl}
            alt={label}
            className="nlbv2-outfit-card-img"
            onClick={() => onLightbox && onLightbox(data.imageUrl)}
            title="Click to enlarge"
          />
        ) : (
          <div className="nlbv2-outfit-card-placeholder">
            {status === 'in_progress' ? '⏳' : '○'}
          </div>
        )}
      </div>
      {hasGeneratedImage && (
        <div className="nlbv2-outfit-card-footer">
          <span className="nlbv2-outfit-card-date">{data?.generatedAt ? formatDate(data.generatedAt) : 'Generated'}</span>
          <div className="nlbv2-outfit-card-actions">
            {/* Approve/reject only when image exists and not yet published */}
            {status === 'done' && publishStatus !== 'published' && approvalStatus !== 'rejected' && (
              approvalStatus === 'approved' ? (
                <button className="nlbv2-btn nlbv2-btn--sm nlbv2-btn--danger" disabled={approving} onClick={handleUnapprove} title="Remove from queue">
                  {approving ? '…' : 'Unapprove'}
                </button>
              ) : (
                <button className="nlbv2-btn nlbv2-btn--sm nlbv2-btn--success" disabled={approving} onClick={handleApprove} title="Add to publish queue">
                  {approving ? '…' : 'Approve'}
                </button>
              )
            )}
            {status === 'done' && publishStatus !== 'published' && (
              approvalStatus === 'rejected' ? (
                <button className="nlbv2-btn nlbv2-btn--sm" disabled={approving} onClick={handleUndoReject} title="Allow this outfit again">
                  {approving ? '…' : 'Undo Reject'}
                </button>
              ) : (
                <button className="nlbv2-btn nlbv2-btn--sm nlbv2-btn--danger" disabled={approving} onClick={handleReject} title="Never generate this outfit again for this product">
                  {approving ? '…' : 'Reject'}
                </button>
              )
            )}
            {approvalStatus !== 'rejected' && (
              <button className="nlbv2-btn nlbv2-btn--sm" disabled={isGenerating} onClick={handleGenerate}>
                {isGenerating ? '…' : 'Redo'}
              </button>
            )}
          </div>
        </div>
      )}
      {!hasGeneratedImage && status !== 'in_progress' && !isGenerating && (
        <div className="nlbv2-outfit-card-footer">
          <button className="nlbv2-btn nlbv2-btn--sm nlbv2-btn--primary" onClick={handleGenerate}>
            Generate
          </button>
        </div>
      )}
      {(status === 'in_progress' || isGenerating) && !hasGeneratedImage && (
        <div className="nlbv2-outfit-card-footer">
          <span style={{ fontSize: 11, color: '#888' }}>Generating…</span>
        </div>
      )}
      {data?.lastError && (
        <div className="nlbv2-outfit-card-error" title={data.lastError}>
          ⚠ {data.lastError.slice(0, 50)}{data.lastError.length > 50 ? '…' : ''}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FabricDetailPanel
// ---------------------------------------------------------------------------

function FabricDetailPanel({ fabric, outfitTypes, generating, onGenerated, onApprovalChange, onClose, authedFetch, onLightbox }) {
  const rowId = getLookbookV2RowId(fabric);
  const fabricSpecId = getLookbookV2FabricSpecId(fabric);
  const eligibleCodes = fabric.eligibleOutfitCodes || [];
  const activeEligibleCodes = getActiveEligibleCodes(fabric);

  // Determine which categories this fabric has eligible codes for
  const availableCategories = useMemo(() => {
    return CATEGORY_ORDER.filter((cat) =>
      OUTFIT_CATEGORIES[cat].some((code) => eligibleCodes.includes(code))
    );
  }, [eligibleCodes]);

  const [activeCat, setActiveCat] = useState(() => availableCategories[0] || 'ethnic');

  // Update active cat if availableCategories changes
  useEffect(() => {
    if (!availableCategories.includes(activeCat) && availableCategories.length > 0) {
      setActiveCat(availableCategories[0]);
    }
  }, [availableCategories, activeCat]);

  const codesForCat = useMemo(() => {
    return OUTFIT_CATEGORIES[activeCat]?.filter((code) => eligibleCodes.includes(code)) || [];
  }, [activeCat, eligibleCodes]);

  const gen = fabric.generation || {};
  const nextOutfit = getNextPendingOutfit(fabric);
  const isGeneratingNext = nextOutfit && rowId && generating.has(getLookbookV2GenerateKey(rowId, nextOutfit));

  if (fabric?._detailLoading) {
    return (
      <div className="nlbv2-panel">
        <div className="nlbv2-panel-header">
          <div className="nlbv2-panel-title">
            <strong>{fabric.fabric}</strong>
            <span className="nlbv2-panel-meta">{fabric.productTitle || fabric.skuPrefix || 'Loading details…'}</span>
          </div>
          <button className="nlbv2-panel-close" onClick={onClose}>✕</button>
        </div>
        <div className="nlbv2-panel-body">
          <div className="nlbv2-loading">Loading product details…</div>
        </div>
      </div>
    );
  }

  const handleGenerateNext = async () => {
    if (!nextOutfit || !fabricSpecId) return;
    try {
      const res = await authedFetch('/api/v1/lookbook-v2/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rowId,
          fabricSpecId,
          productId: fabric.productId || undefined,
          variantId: fabric.variantId || undefined,
          ownerSku: fabric.skuPrefix || undefined,
        }),
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data.success && onGenerated) onGenerated(rowId, fabricSpecId, data.outfitType || nextOutfit, data);
    } catch (_) {}
  };

  return (
    <div className="nlbv2-panel">
      <div className="nlbv2-panel-header">
        <div className="nlbv2-panel-title">
          <strong>{fabric.fabric}</strong>
          <span className="nlbv2-panel-meta">
            {fabric.skuPrefix}
            {' · '}
            {fabric.doneCount}/{activeEligibleCodes.length} done
            {fabric.productTitle && ` · ${fabric.productTitle}`}
          </span>
        </div>
        <button className="nlbv2-panel-close" onClick={onClose}>✕</button>
      </div>
      <div className="nlbv2-panel-body">
        {fabric.productImageUrl && (
          <img
            src={fabric.productImageUrl}
            alt={fabric.fabric}
            className="nlbv2-panel-product-img"
          />
        )}

        {availableCategories.length > 0 ? (
          <>
            <div className="nlbv2-cat-tabs">
              {availableCategories.map((cat) => (
                <button
                  key={cat}
                  className={`nlbv2-cat-tab${activeCat === cat ? ' nlbv2-cat-tab--active' : ''}`}
                  onClick={() => setActiveCat(cat)}
                >
                  {CATEGORY_LABELS[cat]}
                  <span className="nlbv2-cat-tab-count">
                    {OUTFIT_CATEGORIES[cat].filter((c) => eligibleCodes.includes(c) && gen[c]?.approvalStatus !== 'rejected' && gen[c]?.status === 'done').length}
                    /
                    {OUTFIT_CATEGORIES[cat].filter((c) => eligibleCodes.includes(c) && gen[c]?.approvalStatus !== 'rejected').length}
                  </span>
                </button>
              ))}
            </div>
            <div className="nlbv2-outfit-cards">
              {codesForCat.map((code) => {
                const label = getOutfitLabel(code, outfitTypes);
                return (
                  <OutfitCard
                    key={code}
                    code={code}
                    label={label}
                    data={gen[code]}
                    rowId={rowId}
                    fabricId={fabricSpecId}
                    productId={fabric.productId}
                    outfitTypes={outfitTypes}
                    onGenerated={onGenerated}
                    onApprovalChange={onApprovalChange}
                    authedFetch={authedFetch}
                    isGenerating={rowId ? generating.has(getLookbookV2GenerateKey(rowId, code)) : false}
                    onLightbox={onLightbox}
                  />
                );
              })}
            </div>
          </>
        ) : (
          <div className="nlbv2-panel-empty">No outfit types configured for this fabric.</div>
        )}

        <div className="nlbv2-panel-footer">
          {nextOutfit ? (
            <button
              className="nlbv2-generate-next-btn"
              onClick={handleGenerateNext}
              disabled={isGeneratingNext || !fabricSpecId}
            >
              {isGeneratingNext
                ? 'Generating random next outfit…'
                : 'Generate Random Next'}
            </button>
          ) : activeEligibleCodes.length > 0 || eligibleCodes.length > 0 ? (
            <div style={{ textAlign: 'center', color: '#52c41a', fontWeight: 600, fontSize: 14, padding: '8px 0' }}>
              ✓ All outfit types complete
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// OutfitDots (mini status dots for table row)
// ---------------------------------------------------------------------------

function OutfitDots({ fabric }) {
  const codes = (fabric.eligibleOutfitCodes || []).slice(0, 8);
  const gen = fabric.generation || {};
  if (!codes.length) return <span className="nlbv2-not-configured-chip">Not configured</span>;
  return (
    <div className="nlbv2-outfit-dots">
      {codes.map((code) => {
        const s = gen[code]?.status || 'pending';
        const isRejected = gen[code]?.approvalStatus === 'rejected';
        const cls =
          isRejected
            ? 'nlbv2-outfit-dot--rejected'
            : s === 'done'
            ? 'nlbv2-outfit-dot--done'
            : s === 'in_progress'
            ? 'nlbv2-outfit-dot--inprogress'
            : 'nlbv2-outfit-dot--pending';
        return <span key={code} className={`nlbv2-outfit-dot ${cls}`} title={`${code}: ${isRejected ? 'rejected' : s}`} />;
      })}
      {(fabric.eligibleOutfitCodes || []).length > 8 && (
        <span className="nlbv2-outfit-dot-more">+{(fabric.eligibleOutfitCodes || []).length - 8}</span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page component: NewLookbookV2Page
// ---------------------------------------------------------------------------

export default function NewLookbookV2Page() {
  const { currentUser } = useAuth();

  const [outfitTypes, setOutfitTypes] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [collections, setCollections] = useState([]);
  const [scopeType, setScopeType] = useState('');
  const [scopeValue, setScopeValue] = useState('');
  const [fabrics, setFabrics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [selectedFabric, setSelectedFabric] = useState(null);
  const [generating, setGenerating] = useState(new Set());
  const [publishQueueRefreshToken, setPublishQueueRefreshToken] = useState(0);
  const [batchPublishing, setBatchPublishing] = useState(false);
  const [batchPublishMessage, setBatchPublishMessage] = useState('');
  const [generatedReviewRefreshToken, setGeneratedReviewRefreshToken] = useState(0);
  const [configuringFabricId, setConfiguringFabricId] = useState('');
  const [configurableFabrics, setConfigurableFabrics] = useState([]);
  const [loadingConfigurableFabrics, setLoadingConfigurableFabrics] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [seedMessage, setSeedMessage] = useState('');
  const [queue, setQueue] = useState(null);
  const lastQueueProcessedRef = useRef('');

  // ---------------------------------------------------------------------------
  // authedFetch (exact V1 pattern)
  // ---------------------------------------------------------------------------

  const authedFetch = useCallback(async (url, options = {}) => {
    if (!currentUser) throw new Error('Not authenticated.');
    const token = await currentUser.getIdToken();
    const base = String(process.env.REACT_APP_API_BASE_URL || '').trim();
    const resolved = base && url.startsWith('/') ? `${base}${url}` : url;
    return fetch(resolved, {
      ...options,
      headers: { Authorization: `Bearer ${token}`, ...(options.headers || {}) },
    });
  }, [currentUser]);

  // ---------------------------------------------------------------------------
  // Load outfit types, product types, collections concurrently on mount
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const [otRes, ptRes, colRes] = await Promise.all([
          authedFetch('/api/v1/lookbook-v2/outfit-types'),
          authedFetch('/api/v1/lookbook-v2/product-types'),
          authedFetch('/api/v1/lookbook-v2/collections'),
        ]);
        if (otRes.ok) {
          const d = await otRes.json();
          if (d.success) setOutfitTypes(d.outfitTypes || []);
        }
        if (ptRes.ok) {
          const d = await ptRes.json();
          if (d.success) setProductTypes(d.types || d.productTypes || []);
        }
        if (colRes.ok) {
          const d = await colRes.json();
          if (d.success) setCollections(d.collections || []);
        }
      } catch (_) {}
    };
    loadMeta();
  }, [authedFetch]);

  // ---------------------------------------------------------------------------
  // Load fabrics
  // ---------------------------------------------------------------------------

  const fetchFabrics = useCallback(async () => {
    if (!scopeValue) return;
    setLoading(true);
    setError('');
    try {
      const param =
        scopeType === 'productType'
          ? `productType=${encodeURIComponent(scopeValue)}`
          : `collectionId=${encodeURIComponent(scopeValue)}`;
      const res = await authedFetch(`/api/v1/lookbook-v2/fabrics?${param}`);
      if (!res.ok) throw new Error('Failed to load fabrics.');
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Failed to load fabrics.');
      setFabrics(data.fabrics || []);
    } catch (err) {
      setError(err.message || 'Failed to load fabrics.');
    } finally {
      setLoading(false);
    }
  }, [authedFetch, scopeType, scopeValue]);

  const fetchConfigurableFabrics = useCallback(async () => {
    if (!scopeValue) return [];
    const param =
      scopeType === 'productType'
        ? `productType=${encodeURIComponent(scopeValue)}`
        : `collectionId=${encodeURIComponent(scopeValue)}`;
    const res = await authedFetch(`/api/v1/lookbook-v2/configurable-fabrics?${param}`);
    if (!res.ok) throw new Error('Failed to load configurable fabrics.');
    const data = await res.json();
    if (!data.success) throw new Error(data.message || 'Failed to load configurable fabrics.');
    return data.fabrics || [];
  }, [authedFetch, scopeType, scopeValue]);

  const openConfigureModal = useCallback(async () => {
    if (!scopeValue) return;
    setLoadingConfigurableFabrics(true);
    setError('');
    try {
      const items = await fetchConfigurableFabrics();
      if (!items.length) {
        setError('No fabric specs found for this scope.');
        setConfigurableFabrics([]);
        setConfiguringFabricId('');
        return;
      }
      setConfigurableFabrics(items);
      setConfiguringFabricId(getLookbookV2FabricSpecId(items[0]) || String(items[0]?.id || '').trim() || '');
    } catch (err) {
      setError(err.message || 'Failed to load configurable fabrics.');
    } finally {
      setLoadingConfigurableFabrics(false);
    }
  }, [fetchConfigurableFabrics, scopeValue]);

  const resolveProductDetail = useCallback(async (fabric) => {
    if (!fabric?.productId) return fabric;
    const params = new URLSearchParams({ productId: String(fabric.productId) });
    if (fabric.variantId) params.set('variantId', String(fabric.variantId));
    if (scopeType === 'productType' && scopeValue) params.set('productType', scopeValue);
    if (scopeType === 'collection' && scopeValue) params.set('collectionId', scopeValue);
    const res = await authedFetch(`/api/v1/lookbook-v2/product-detail?${params.toString()}`);
    if (!res.ok) throw new Error('Failed to load product detail.');
    const data = await res.json();
    if (!data.success || !data.fabric) throw new Error(data.message || 'Failed to load product detail.');
    return mergeLookbookV2Rows(fabric, data.fabric);
  }, [authedFetch, scopeType, scopeValue]);

  const hydrateFabricDetail = useCallback(async (fabric, options = {}) => {
    if (!fabric?.productId) {
      if (options.select !== false) setSelectedFabric(fabric);
      return fabric;
    }
    const rowId = getLookbookV2RowId(fabric);
    const loadingRow = { ...fabric, _detailLoading: true };
    if (options.select !== false) setSelectedFabric(loadingRow);
    setFabrics((prev) => prev.map((item) => (getLookbookV2RowId(item) === rowId ? loadingRow : item)));
    try {
      const detailed = await resolveProductDetail(fabric);
      setFabrics((prev) => prev.map((item) => (getLookbookV2RowId(item) === rowId ? detailed : item)));
      if (options.select !== false) setSelectedFabric(detailed);
      return detailed;
    } catch (err) {
      const reverted = { ...fabric, _detailLoading: false, _detailError: err.message || 'Failed to load detail.' };
      setFabrics((prev) => prev.map((item) => (getLookbookV2RowId(item) === rowId ? reverted : item)));
      if (options.select !== false) setSelectedFabric(reverted);
      return reverted;
    }
  }, [resolveProductDetail]);

  useEffect(() => {
    setPage(1);
    setSelectedFabric(null);
    setConfiguringFabricId('');
    setConfigurableFabrics([]);
    fetchFabrics();
  }, [fetchFabrics]);

  // Auto-queue scope sync removed — generation fully managed by Cloud Scheduler.

  // ---------------------------------------------------------------------------
  // Queue change handler
  // ---------------------------------------------------------------------------

  const handleQueueChange = useCallback((q) => {
    setQueue(q);
    const processedRowId = String(q?.lastProcessedFabricId || '').trim();
    const processedAt = String(q?.lastProcessedAt || '').trim();
    const processedOutfitCode = String(q?.lastProcessedOutfitType || '').trim();
    if (!processedRowId || !processedAt) return;
    const marker = `${processedRowId}:${processedOutfitCode}:${processedAt}`;
    if (lastQueueProcessedRef.current === marker) return;
    lastQueueProcessedRef.current = marker;
    const targetRow = fabrics.find((item) => getLookbookV2RowId(item) === processedRowId);
    if (!targetRow) return;
    const shouldSelect = getLookbookV2RowId(selectedFabric) === processedRowId;
    hydrateFabricDetail(targetRow, { select: shouldSelect });
  }, [fabrics, hydrateFabricDetail, selectedFabric]);

  // ---------------------------------------------------------------------------
  // Scope change
  // ---------------------------------------------------------------------------

  const handleScopeChange = (type, value) => {
    setScopeType(type);
    setScopeValue(value);
    setStatusFilter('all');
    setPage(1);
    setSelectedFabric(null);
    setBatchPublishMessage('');
  };

  const handlePushBatch = useCallback(async () => {
    if (scopeType !== 'productType' || !scopeValue) return;
    setBatchPublishing(true);
    setBatchPublishMessage('');
    try {
      const res = await authedFetch('/api/v1/lookbook-v2/publish-queue/push-batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productType: scopeValue, limit: 20 }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        throw new Error(data?.message || 'Batch publish failed.');
      }
      const published = Number(data.published || 0);
      const failed = Number(data.failed || 0);
      const processed = Number(data.processed || 0);
      setBatchPublishMessage(`Processed ${processed}. Published ${published}. Failed ${failed}.`);
      setPublishQueueRefreshToken((prev) => prev + 1);
      await fetchFabrics();
    } catch (error) {
      setBatchPublishMessage(error?.message || 'Batch publish failed.');
    } finally {
      setBatchPublishing(false);
    }
  }, [authedFetch, fetchFabrics, scopeType, scopeValue]);

  // ---------------------------------------------------------------------------
  // Generate handling (optimistic updates)
  // ---------------------------------------------------------------------------

  const handleGenerateStart = useCallback((rowId, fabricId, outfitCode) => {
    const generateKey = getLookbookV2GenerateKey(rowId, outfitCode);
    setGenerating((prev) => new Set([...prev, generateKey]));
    setFabrics((prev) =>
      prev.map((f) => {
        if (getLookbookV2RowId(f) !== rowId) return f;
        return {
          ...f,
          generation: {
            ...f.generation,
            [outfitCode]: { ...f.generation?.[outfitCode], status: 'in_progress' },
          },
        };
      })
    );
    setSelectedFabric((prev) => {
      if (!prev || getLookbookV2RowId(prev) !== rowId) return prev;
      return {
        ...prev,
        generation: {
          ...prev.generation,
          [outfitCode]: { ...prev.generation?.[outfitCode], status: 'in_progress' },
        },
      };
    });
  }, []);

  const handleGenerated = useCallback((rowId, fabricId, outfitCode, result) => {
    const generateKey = getLookbookV2GenerateKey(rowId, outfitCode);
    setGenerating((prev) => {
      const next = new Set(prev);
      next.delete(generateKey);
      return next;
    });
    const updateFabric = (f) => {
      if (getLookbookV2RowId(f) !== rowId) return f;
      const updatedGen = {
        ...f.generation,
        [outfitCode]: {
          ...f.generation?.[outfitCode],
          entryId: result.entryId || f.generation?.[outfitCode]?.entryId || null,
          status: 'done',
          imageUrl: result.imageUrl,
          imagePath: result.imagePath,
          generatedAt: new Date().toISOString(),
          productId: result.productId || f.productId || null,
          variantId: result.variantId || f.variantId || null,
          ownerSku: result.ownerSku || f.skuPrefix || null,
        },
      };
      const activeCodes = getActiveEligibleCodes({ ...f, generation: updatedGen });
      const doneCount = activeCodes.filter((c) => updatedGen[c]?.status === 'done').length;
      return { ...f, generation: updatedGen, doneCount, totalCount: activeCodes.length };
    };
    setFabrics((prev) => prev.map(updateFabric));
    setSelectedFabric((prev) => (prev && getLookbookV2RowId(prev) === rowId ? updateFabric(prev) : prev));
    setGeneratedReviewRefreshToken((prev) => prev + 1);
  }, []);

  const handleGenerateRevert = useCallback((rowId, fabricId, outfitCode) => {
    const generateKey = getLookbookV2GenerateKey(rowId, outfitCode);
    setGenerating((prev) => {
      const next = new Set(prev);
      next.delete(generateKey);
      return next;
    });
    setFabrics((prev) =>
      prev.map((f) => {
        if (getLookbookV2RowId(f) !== rowId) return f;
        return {
          ...f,
          generation: {
            ...f.generation,
            [outfitCode]: { ...f.generation?.[outfitCode], status: 'pending' },
          },
        };
      })
    );
    setSelectedFabric((prev) => {
      if (!prev || getLookbookV2RowId(prev) !== rowId) return prev;
      return {
        ...prev,
        generation: {
          ...prev.generation,
          [outfitCode]: { ...prev.generation?.[outfitCode], status: 'pending' },
        },
      };
    });
  }, []);

  // Approval state update (optimistic)
  const handleApprovalChange = useCallback((rowId, fabricId, outfitCode, patch = {}) => {
    const updateFabric = (f) => {
      if (getLookbookV2RowId(f) !== rowId) return f;
      const nextGeneration = {
        ...f.generation,
        [outfitCode]: {
          ...f.generation?.[outfitCode],
          ...patch,
        },
      };
      const activeCodes = getActiveEligibleCodes({ ...f, generation: nextGeneration });
      return {
        ...f,
        generation: nextGeneration,
        doneCount: activeCodes.filter((c) => nextGeneration[c]?.status === 'done').length,
        totalCount: activeCodes.length,
      };
    };
    setFabrics((prev) => prev.map(updateFabric));
    setSelectedFabric((prev) => (prev && getLookbookV2RowId(prev) === rowId ? updateFabric(prev) : prev));
    setPublishQueueRefreshToken((prev) => prev + 1);
    setGeneratedReviewRefreshToken((prev) => prev + 1);
  }, []);

  // Intercept generate calls from child components to also set in_progress state
  const wrappedAuthedFetch = useCallback(
    async (url, options = {}) => {
      if (url.includes('/api/v1/lookbook-v2/generate') && options.method === 'POST') {
        try {
          const body = JSON.parse(options.body || '{}');
          if (body.rowId && body.fabricSpecId && body.outfitCode) {
            handleGenerateStart(body.rowId, body.fabricSpecId, body.outfitCode);
          }
        } catch (_) {}
      }
      const res = await authedFetch(url, options);
      // On generate failure, revert
      if (url.includes('/api/v1/lookbook-v2/generate') && options.method === 'POST' && !res.ok) {
        try {
          const body = JSON.parse(options.body || '{}');
          if (body.rowId && body.fabricSpecId && body.outfitCode) {
            handleGenerateRevert(body.rowId, body.fabricSpecId, body.outfitCode);
          }
        } catch (_) {}
      }
      return res;
    },
    [authedFetch, handleGenerateStart, handleGenerateRevert]
  );

  // ---------------------------------------------------------------------------
  // Migrate Flat Lay → Fabric Editorial
  // ---------------------------------------------------------------------------

  const [migrating, setMigrating] = useState(false);
  const [migrateMessage, setMigrateMessage] = useState('');

  const handleMigrateFlatLay = async () => {
    if (!window.confirm('Swap FLAT_LAY → FABRIC_EDITORIAL on all existing fabric specs in Firestore? This cannot be undone.')) return;
    setMigrating(true);
    setMigrateMessage('');
    try {
      const res = await authedFetch('/api/v1/lookbook-v2/migrate-flat-lay', { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setMigrateMessage(`Migrated ${data.updated} specs.`);
        await fetchFabrics();
      } else {
        setMigrateMessage(data.message || 'Migration failed.');
      }
    } catch (_) {
      setMigrateMessage('Migration failed.');
    } finally {
      setMigrating(false);
      setTimeout(() => setMigrateMessage(''), 6000);
    }
  };

  // ---------------------------------------------------------------------------
  // Seed outfit types
  // ---------------------------------------------------------------------------

  const handleSeed = async (force = false) => {
    if (force && !window.confirm('Re-seed will OVERWRITE existing outfit type assignments for all fabrics in the seed map. Continue?')) return;
    setSeeding(true);
    setSeedMessage('');
    try {
      const url = force ? '/api/v1/lookbook-v2/seed-outfit-types?force=true' : '/api/v1/lookbook-v2/seed-outfit-types';
      const res = await authedFetch(url, { method: 'POST' });
      if (!res.ok) { setSeedMessage('Seed failed.'); return; }
      const data = await res.json();
      if (data.success) {
        setSeedMessage(
          `${force ? 'Force re-seeded' : 'Seeded'} ${data.seeded ?? 0} fabrics, skipped ${data.skipped ?? 0}.`
        );
        await fetchFabrics();
      } else {
        setSeedMessage(data.message || 'Seed failed.');
      }
    } catch (_) {
      setSeedMessage('Seed failed.');
    } finally {
      setSeeding(false);
      setTimeout(() => setSeedMessage(''), 5000);
    }
  };

  // ---------------------------------------------------------------------------
  // Filtering + pagination
  // ---------------------------------------------------------------------------

  const filteredFabrics = useMemo(() => {
    if (statusFilter === 'all') return fabrics;
    if (statusFilter === 'published') return fabrics.filter((f) => hasPublishedOutfit(f));
    if (statusFilter === 'pushpending') return fabrics.filter((f) => hasPushPendingOutfit(f));
    if (statusFilter === 'approvepending') return fabrics.filter((f) => hasApprovePendingOutfit(f));
    return fabrics.filter((f) => getFabricOverallStatus(f) === statusFilter);
  }, [fabrics, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredFabrics.length / PAGE_SIZE));
  const pagedFabrics = filteredFabrics.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const statusCounts = useMemo(() => {
    const counts = {
      all: fabrics.length,
      complete: 0,
      inprogress: 0,
      published: 0,
      pushpending: 0,
      approvepending: 0,
      notstarted: 0,
    };
    fabrics.forEach((f) => {
      counts[getFabricOverallStatus(f)]++;
      if (hasPublishedOutfit(f)) counts.published++;
      if (hasPushPendingOutfit(f)) counts.pushpending++;
      if (hasApprovePendingOutfit(f)) counts.approvepending++;
    });
    return counts;
  }, [fabrics]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="nlbv2-layout">
      <div className={`nlbv2-main${selectedFabric ? ' nlbv2-main--with-panel' : ''}`}>
        {/* Header */}
        <div className="nlbv2-header">
          <div className="nlbv2-header-left">
            <h1 className="nlbv2-title">New Lookbook V2</h1>
          </div>
          <div className="nlbv2-header-right">
            {seedMessage && <span className="nlbv2-seed-msg">{seedMessage}</span>}
            {migrateMessage && <span className="nlbv2-seed-msg" style={{ color: '#d48806' }}>{migrateMessage}</span>}
            <button
              className="nlbv2-btn nlbv2-btn--sm"
              onClick={() => handleSeed(false)}
              disabled={seeding}
              title="Seed outfit types for fabrics not yet configured"
            >
              {seeding ? 'Seeding…' : 'Seed Outfit Types'}
            </button>
            <button
              className="nlbv2-btn nlbv2-btn--sm nlbv2-btn--danger"
              onClick={() => handleSeed(true)}
              disabled={seeding}
              title="Force overwrite outfit types for ALL fabrics in seed map"
            >
              {seeding ? 'Seeding…' : 'Re-seed (Force)'}
            </button>
            <button
              className="nlbv2-btn nlbv2-btn--sm"
              onClick={handleMigrateFlatLay}
              disabled={migrating}
              title="One-time: swap FLAT_LAY → FABRIC_EDITORIAL on all specs"
            >
              {migrating ? 'Migrating…' : 'Migrate Flat Lay'}
            </button>
            <button
              className="nlbv2-btn nlbv2-btn--sm"
              onClick={openConfigureModal}
              disabled={!scopeValue || loadingConfigurableFabrics}
              title={!scopeValue ? 'Select a product type or collection first' : 'Configure fabric specs for this scope'}
            >
              {loadingConfigurableFabrics ? 'Loading…' : 'Configure Fabrics'}
            </button>
            <button
              className="nlbv2-btn nlbv2-btn--sm"
              onClick={fetchFabrics}
              disabled={loading || !scopeValue}
            >
              {loading ? 'Loading…' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Scope Selector */}
        <ScopeSelector
          scopeType={scopeType}
          scopeValue={scopeValue}
          productTypes={productTypes}
          collections={collections}
          onChange={handleScopeChange}
          onPushBatch={handlePushBatch}
          batchPublishing={batchPublishing}
          batchPublishMessage={batchPublishMessage}
        />

        {/* Auto-Queue Panel */}
        <AutoQueuePanel
          authedFetch={authedFetch}
          onQueueChange={handleQueueChange}
          outfitTypes={outfitTypes}
          scopeType={scopeType}
          scopeValue={scopeValue}
        />

        {/* Publish Queue Status */}
        <PublishQueuePanel authedFetch={authedFetch} refreshToken={publishQueueRefreshToken} />

        {/* Generated Review */}
        <GeneratedReviewAccordion
          scopeType={scopeType}
          scopeValue={scopeValue}
          authedFetch={wrappedAuthedFetch}
          outfitTypes={outfitTypes}
          generating={generating}
          onGenerated={handleGenerated}
          onApprovalChange={handleApprovalChange}
          onLightbox={setLightboxUrl}
          refreshToken={generatedReviewRefreshToken}
        />

        {/* Prompt Editor */}
        <PromptEditor outfitTypes={outfitTypes} authedFetch={authedFetch} />

        {/* No scope selected placeholder */}
        {!scopeValue && (
          <div className="nlbv2-placeholder">
            Select Product Type or Collection first, then choose a value to load products.
          </div>
        )}

        {scopeValue && (
          <>
            {/* Filter pills */}
            <div className="nlbv2-filter-pills">
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f.key}
                  className={`nlbv2-pill${statusFilter === f.key ? ' nlbv2-pill--active' : ''}`}
                  onClick={() => { setStatusFilter(f.key); setPage(1); }}
                >
                  {f.label}
                  <span className="nlbv2-pill-count">{statusCounts[f.key] ?? 0}</span>
                </button>
              ))}
            </div>

            {error && <div className="nlbv2-error">{error}</div>}

            {loading ? (
              <div className="nlbv2-loading">Loading fabrics…</div>
            ) : (
              <>
                <div className="nlbv2-table-meta">
                  {filteredFabrics.length} variant{filteredFabrics.length !== 1 ? 's' : ''}
                  {' · '}
                  {scopeType === 'productType'
                    ? scopeValue
                    : collections.find((c) => c.id === scopeValue)?.title || scopeValue}
                </div>
                <div className="nlbv2-table-wrap">
                  <table className="nlbv2-table">
                    <thead>
                      <tr>
                        <th></th>
                        <th>Fabric</th>
                        <th>Type</th>
                        <th>Progress</th>
                        <th>Outfits</th>
                        <th>Status</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedFabrics.length === 0 && (
                        <tr>
                          <td colSpan={7} className="nlbv2-empty">
                            No products match this filter.
                          </td>
                        </tr>
                      )}
                      {pagedFabrics.map((fabric) => {
                        const rowId = getLookbookV2RowId(fabric);
                        const fabricSpecId = getLookbookV2FabricSpecId(fabric);
                        const status = getFabricOverallStatus(fabric);
                        const isSelected = getLookbookV2RowId(selectedFabric) === rowId;
                        const nextOutfit = getNextPendingOutfit(fabric);
                        const activeEligibleCodes = getActiveEligibleCodes(fabric);
                        const hasEligible = activeEligibleCodes.length > 0;
                        const isAnyGenerating = activeEligibleCodes.some((c) =>
                          rowId ? generating.has(getLookbookV2GenerateKey(rowId, c)) : false
                        );
                        const canResolveOnGenerate = !fabric.hasFabricSpec && Boolean(fabric.productId);
                        const totalCount = activeEligibleCodes.length;
                        const doneCount = fabric.doneCount ?? 0;

                        return (
                          <tr
                            key={rowId}
                            className={`nlbv2-row nlbv2-row--clickable${isSelected ? ' nlbv2-row--selected' : ''}`}
                            onClick={() => hydrateFabricDetail(fabric)}
                          >
                            {/* Thumbnail */}
                            <td className="nlbv2-thumb-cell">
                              {fabric.productImageUrl ? (
                                <img src={fabric.productImageUrl} alt="" className="nlbv2-thumb" />
                              ) : (
                                <div className="nlbv2-thumb nlbv2-thumb--empty" />
                              )}
                            </td>

                            {/* Fabric name + SKU */}
                            <td>
                              <div className="nlbv2-fabric-title">{fabric.fabric}</div>
                              <div className="nlbv2-fabric-sku">{fabric.skuPrefix || '—'}</div>
                            </td>

                            {/* Fabric type */}
                            <td>
                              <span className="nlbv2-fabric-type">{fabric.type || '—'}</span>
                            </td>

                            {/* Progress bar */}
                            <td>
                              {hasEligible ? (
                                <div className="nlbv2-mini-bar-wrap">
                                  <div className="nlbv2-mini-bar-bg">
                                    <div
                                      className="nlbv2-mini-bar-fill"
                                      style={{ width: totalCount > 0 ? `${(doneCount / totalCount) * 100}%` : '0%' }}
                                    />
                                  </div>
                                  <span className="nlbv2-mini-bar-label">{doneCount}/{totalCount}</span>
                                </div>
                              ) : (
                                <span
                                  className="nlbv2-not-configured"
                                  title={fabricSpecId ? 'Configure from the top Configure Fabrics button' : 'No fabric specification linked to this product'}
                                >
                                  {fabricSpecId ? 'Not configured' : 'No fabric spec'}
                                </span>
                              )}
                            </td>

                            {/* Status dots */}
                            <td>
                              <OutfitDots fabric={fabric} />
                            </td>

                            {/* Status badge */}
                            <td>
                              <StatusBadge status={status} />
                            </td>

                            {/* Actions */}
                            <td
                              style={{ textAlign: 'right', whiteSpace: 'nowrap' }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {nextOutfit && (
                                <button
                                  className="nlbv2-btn nlbv2-btn--primary nlbv2-btn--sm"
                                  disabled={isAnyGenerating || !fabricSpecId}
                                  onClick={async () => {
                                    if (!fabricSpecId) return;
                                    handleGenerateStart(rowId, fabricSpecId, nextOutfit);
                                    try {
                                      const res = await authedFetch('/api/v1/lookbook-v2/generate', {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({
                                          rowId,
                                          fabricSpecId,
                                          outfitCode: nextOutfit,
                                          productId: fabric.productId || undefined,
                                          variantId: fabric.variantId || undefined,
                                          ownerSku: fabric.skuPrefix || undefined,
                                        }),
                                      });
                                      if (!res.ok) { handleGenerateRevert(rowId, fabricSpecId, nextOutfit); return; }
                                      const data = await res.json();
                                      if (data.success) handleGenerated(rowId, fabricSpecId, nextOutfit, data);
                                      else handleGenerateRevert(rowId, fabricSpecId, nextOutfit);
                                    } catch (_) {
                                      handleGenerateRevert(rowId, fabricSpecId, nextOutfit);
                                    }
                                  }}
                                  style={{ marginRight: 4 }}
                                >
                                  {isAnyGenerating ? 'Generating…' : 'Generate'}
                                </button>
                              )}
                              {!nextOutfit && canResolveOnGenerate && (
                                <button
                                  className="nlbv2-btn nlbv2-btn--primary nlbv2-btn--sm"
                                  onClick={async () => {
                                    try {
                                      const res = await authedFetch('/api/v1/lookbook-v2/generate', {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({
                                          productId: fabric.productId,
                                          productType: scopeType === 'productType' ? scopeValue : undefined,
                                          variantId: fabric.variantId || undefined,
                                          ownerSku: fabric.skuPrefix || undefined,
                                        }),
                                      });
                                      if (!res.ok) return;
                                      await hydrateFabricDetail(fabric, { select: isSelected });
                                    } catch (_) {}
                                  }}
                                  style={{ marginRight: 4 }}
                                >
                                  Initialize & Generate
                                </button>
                              )}
                              {status === 'complete' && !nextOutfit && (
                                <span style={{ fontSize: 12, color: '#52c41a', marginRight: 4 }}>✓</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <Pagination page={page} totalPages={totalPages} onChange={setPage} />
              </>
            )}
          </>
        )}
      </div>

      {/* Right detail panel */}
      {selectedFabric && (
        <FabricDetailPanel
          fabric={selectedFabric}
          outfitTypes={outfitTypes}
          generating={generating}
          onGenerated={handleGenerated}
          onApprovalChange={handleApprovalChange}
          onClose={() => setSelectedFabric(null)}
          authedFetch={wrappedAuthedFetch}
          onLightbox={setLightboxUrl}
        />
      )}

      {/* Configure Outfits Overlay */}
      {configuringFabricId && (
        <ConfigureOutfitsModal
          fabrics={configurableFabrics}
          initialFabricId={configuringFabricId}
          outfitTypes={outfitTypes}
          authedFetch={authedFetch}
          onClose={() => setConfiguringFabricId('')}
          onSaved={async () => {
            const refreshed = await fetchConfigurableFabrics().catch(() => configurableFabrics);
            setConfigurableFabrics(refreshed);
            await fetchFabrics();
          }}
        />
      )}

      {/* Lightbox */}
      {lightboxUrl && (
        <div className="nlbv2-lightbox" onClick={() => setLightboxUrl(null)}>
          <img
            src={lightboxUrl}
            alt="Generated lookbook"
            onClick={(e) => e.stopPropagation()}
          />
          <button className="nlbv2-lightbox-close" onClick={() => setLightboxUrl(null)}>✕</button>
        </div>
      )}
    </div>
  );
}
